/**
 * Kryptering av fritekst i den lokale loggen.
 *
 * KRAVET: CLAUDE.md og docs/09-juridisk.md krever kryptert lagring paa en
 * privat telefon fram til produksjonens `end_date`. Det var ikke oppfylt:
 * arkitekturen pekte paa SQLCipher gjennom `op-sqlite`, men appen er en PWA
 * med `sqlocal` over OPFS, og den veien finnes ikke der.
 *
 * LOESNINGEN SOM FAKTISK ER TILGJENGELIG: AES-GCM gjennom Web Crypto, med en
 * noekkel som er `extractable: false`. Noekkelen lagres som et CryptoKey-objekt
 * i IndexedDB — ikke som bytes. Raa noekkelmateriale finnes aldri i JavaScript
 * og kan ikke leses ut, heller ikke av kode som kjoerer paa samme opphav.
 *
 * HVA DETTE BESKYTTER MOT, OG HVA DET IKKE GJOER. Det beskytter databasefila:
 * en kopi av OPFS-lageret, en sikkerhetskopi eller en filsystemdump er
 * ulesbar uten noekkelen, og noekkelen foelger ikke med fila. Det beskytter
 * IKKE mot noen som har den opplaaste telefonen og aapner appen — men det
 * gjoer heller ikke SQLCipher med noekkel i Keychain. Trusselen er den
 * mistede telefonen, ikke den laante.
 *
 * KUN FRITEKST KRYPTERES. Tidsstempler, tidskodeverdier og id-er staar i
 * klartekst, fordi de sorterer og regner — og fordi de ikke er innholdet.
 * Det personlige ligger i det regi SKRIVER. Ingen spoerring i db.ts bruker
 * fritekst i WHERE eller ORDER BY, saa ingenting gaar tapt paa veien.
 */

const DB = "infoflyt.noekkel";
const LAGER = "noekler";
const ID = "logg.v1";
/** Prefiks saa klartekst fra foer krypteringen fantes kjennes igjen. */
const MERKE = "v1.";

let noekkel: Promise<CryptoKey> | null = null;

function aapneNoekkellager(): Promise<IDBDatabase> {
  return new Promise((løs, avvis) => {
    const b = indexedDB.open(DB, 1);
    b.onupgradeneeded = () => { b.result.createObjectStore(LAGER); };
    b.onsuccess = () => løs(b.result);
    b.onerror = () => avvis(b.error ?? new Error("IndexedDB kunne ikke aapnes"));
  });
}

function les(db: IDBDatabase): Promise<CryptoKey | undefined> {
  return new Promise((løs, avvis) => {
    const f = db.transaction(LAGER, "readonly").objectStore(LAGER).get(ID);
    f.onsuccess = () => løs(f.result as CryptoKey | undefined);
    f.onerror = () => avvis(f.error);
  });
}

function skriv(db: IDBDatabase, k: CryptoKey): Promise<void> {
  return new Promise((løs, avvis) => {
    const t = db.transaction(LAGER, "readwrite");
    t.objectStore(LAGER).put(k, ID);
    t.oncomplete = () => løs();
    t.onerror = () => avvis(t.error);
  });
}

/**
 * Noekkelen. Lages foerste gang, og gjenbrukes.
 *
 * `extractable: false` er hele poenget. Et CryptoKey kan lagres i IndexedDB
 * med strukturert kloning, og flagget foelger med — den som henter det ut
 * igjen faar fortsatt ikke se bytene.
 */
function hentNoekkel(): Promise<CryptoKey> {
  noekkel ??= (async () => {
    const db = await aapneNoekkellager();
    const finnes = await les(db);
    if (finnes !== undefined) return finnes;
    const ny = await crypto.subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    );
    await skriv(db, ny);
    return ny;
  })();
  return noekkel;
}

function tilBase64(b: Uint8Array): string {
  let s = "";
  for (const x of b) s += String.fromCharCode(x);
  return btoa(s);
}

function fraBase64(s: string): Uint8Array<ArrayBuffer> {
  const r = atob(s);
  const b = new Uint8Array(new ArrayBuffer(r.length));
  for (let i = 0; i < r.length; i++) b[i] = r.charCodeAt(i);
  return b;
}

/** Tom tekst krypteres ikke. En markoer uten tekst skjuler ingenting. */
export async function krypter(klartekst: string): Promise<string> {
  if (klartekst === "") return "";
  const k = await hentNoekkel();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ut = new Uint8Array(await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    k,
    new TextEncoder().encode(klartekst),
  ));
  const samlet = new Uint8Array(iv.length + ut.length);
  samlet.set(iv);
  samlet.set(ut, iv.length);
  return MERKE + tilBase64(samlet);
}

/**
 * Klartekst uten merke slippes gjennom.
 *
 * Rader skrevet foer krypteringen fantes skal fortsatt kunne leses. Det gjoer
 * ogsaa at migreringen kan skje i ro og mak i stedet for i ett jafs ved
 * oppstart — en logg som ikke er migrert virker som foer.
 */
export async function dekrypter(lagret: string): Promise<string> {
  if (!lagret.startsWith(MERKE)) return lagret;
  try {
    const k = await hentNoekkel();
    const b = fraBase64(lagret.slice(MERKE.length));
    const ut = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: b.subarray(0, 12) },
      k,
      b.subarray(12),
    );
    return new TextDecoder().decode(ut);
  } catch {
    /*
     * Noekkelen er borte — nettleserdata slettet, eller en annen enhet.
     * Da er teksten tapt, og det skal SES. En tom streng ville sett ut som en
     * markoer uten tekst, og regi ville ikke visst at noe manglet.
     */
    return "[kan ikke leses: nøkkelen mangler]";
  }
}

/** Sann naar verdien alt er kryptert. Brukes av migreringen. */
export function erKryptert(s: string): boolean {
  return s.startsWith(MERKE);
}
